import { useMemo } from 'react';

import { useAppSelector } from 'src/hooks/useStore';
import {
  GetCurrencyFormatter,
  GetDefaultCurrency,
} from 'src/legacy/components/Formatters';
import {
  Invoice,
  PaymentMethodType,
  PaymentSourceType,
} from 'src/services/api/invoicesApi';
import { useGetPaymentFeesQuery } from 'src/services/api/paymentFee';
import { StripePaymentMethod } from 'src/types/payment';

import { getPaymentMethodDetailsFromSource } from '../../../../utils/BillingUtils';

type Props = {
  amountDue: Invoice['total'];
  source?: StripePaymentMethod | string | null;
  preferences: Invoice['paymentPreferences'];
  currency?: string;
};

const DEFAULT_FEE_DETAILS = {
  feeLabel: '',
  total: 0,
};

export const usePaymentProcessingFee = ({
  amountDue,
  source,
  preferences,
  currency,
}: Props) => {
  const { data: feeReference } = useGetPaymentFeesQuery();

  const accountInfo = useAppSelector((state) => state.payments.accountInfo);
  const defaultCurrency = accountInfo?.default_currency || GetDefaultCurrency();
  const formatter = GetCurrencyFormatter(currency || defaultCurrency);
  const formatterOptions = formatter.formatToParts();
  const prefix = formatterOptions.find((opt) => opt.type === 'currency')?.value;

  const absorbCreditCardFees = preferences.absorbTransactionFee.includes(
    PaymentMethodType.CreditCard,
  );

  const absorbACHFee = preferences.absorbTransactionFee.includes(
    PaymentMethodType.BankAccount,
  );

  const ignoreFees = !source && (!absorbCreditCardFees || !absorbACHFee);

  const creditCardFeeDetails = useMemo(() => {
    if (!feeReference) return DEFAULT_FEE_DETAILS;
    // For credit cards, two different sorts of fee exist:
    // - A fixed fee (e.g. $0.30)
    // - A percentage fee based on plan (e.g. 3.1%)

    const ccFeeBaseAmount = (amountDue * feeReference.ccBasePercentage) / 100;
    // credit card fixed fee is saved in USD, so we need to convert it to cents
    const ccFeeFixedAmount = feeReference.ccFixed * 100;

    // No fee for CU if credit card fees are absorbed
    const totalCreditCardFee = absorbCreditCardFees
      ? 0
      : ccFeeBaseAmount + ccFeeFixedAmount;

    // Renders like "3.1% + $0.30"
    const feeLabel = `${feeReference.ccBasePercentage}% ${
      feeReference.ccFixed > 0
        ? `+ ${prefix + feeReference.ccFixed.toFixed(2)}`
        : ''
    }`;
    return {
      feeLabel,
      total: totalCreditCardFee,
    };
  }, [amountDue, feeReference, preferences, prefix]);

  const bankAccountFeeDetails = useMemo(() => {
    if (!feeReference) return DEFAULT_FEE_DETAILS;

    let feeLabel = '';

    // For ACH, fee is either a fixed amount (e.g. $5) or a percentage (e.g. 1%) whichever is lower
    // When fixed amount is 0, it means there is no upper bound for the percentage fee
    const achPercentageFee = (amountDue * feeReference.achPercentage) / 100;
    const achFeeFixedAmount = feeReference.achFixed * 100; // to convert in cents
    feeLabel = `${feeReference.achPercentage}%`;

    if (achPercentageFee >= achFeeFixedAmount && achFeeFixedAmount > 0) {
      feeLabel = `${prefix}${feeReference.achFixed}`;
    }

    // additional there is a percentage fee when the amount
    // exceeds a certain threshold (e.g. .2% when amount > $5000)
    let achThresholdExceedFee = 0;
    const achThreshold = feeReference.achThreshold * 100;
    if (
      achThreshold > 0 &&
      feeReference.achThresholdExceededPercentage &&
      amountDue > achThreshold
    ) {
      feeLabel = `${feeLabel} + ${feeReference.achThresholdExceededPercentage}%`;
      achThresholdExceedFee =
        (amountDue * feeReference.achThresholdExceededPercentage) / 100;
    }

    // No fee for CU if ACH fees are absorbed
    const totalACHFee = absorbACHFee
      ? 0
      : Math.min(achPercentageFee, achFeeFixedAmount) + achThresholdExceedFee;

    return {
      feeLabel,
      total: totalACHFee,
    };
  }, [amountDue, feeReference, preferences, prefix]);

  const feeDetails = useMemo(() => {
    if (!source) return DEFAULT_FEE_DETAILS;
    if (
      source === PaymentSourceType.Card ||
      source === PaymentSourceType.ApplePay ||
      source === PaymentSourceType.GooglePay
    ) {
      return creditCardFeeDetails;
    } else if (
      source === PaymentSourceType.BankAccount ||
      source === 'us_bank_account' // this is the PaymentMethod.type used by stripe for ACH
    ) {
      return bankAccountFeeDetails;
    } else if (typeof source !== 'string') {
      const { card } = getPaymentMethodDetailsFromSource(source);
      return card ? creditCardFeeDetails : bankAccountFeeDetails;
    }

    return DEFAULT_FEE_DETAILS;
  }, [source, creditCardFeeDetails, bankAccountFeeDetails]);

  // Return defaults if fee reference or payment source is not available
  if (!feeReference) {
    return {
      fee: 0,
      label: '',
      creditCardFeeDetails: DEFAULT_FEE_DETAILS,
      bankAccountFeeDetails: DEFAULT_FEE_DETAILS,
      ignoreFees,
    };
  }

  return {
    fee: feeDetails.total,
    label: feeDetails.feeLabel,
    creditCardFeeDetails,
    bankAccountFeeDetails,
    ignoreFees,
  };
};
